import { createDeckProject } from './defaults'
import type { DeckProject, WorkspaceTab } from './types'
import { newId } from './utils'

export const GUIDED_TOUR_STORAGE_KEY = 'deckelier.guided-tour.v1'

export type GuidedTourStepId =
  | 'welcome'
  | 'add-shape'
  | 'size-shape'
  | 'add-text'
  | 'place-text'
  | 'bind-text'
  | 'open-data'
  | 'add-card'
  | 'fill-titles'
  | 'open-render'
  | 'finish'

export interface GuidedTourStep {
  id: GuidedTourStepId
  title: string
  instruction: string
  target: string
  tab?: WorkspaceTab
  continueLabel?: string
  manual?: boolean
}

export interface GuidedTourSession {
  projectId: string
  stepIndex: number
  updatedAt: string
}

interface StorageLike {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

export const GUIDED_TOUR_STEPS: GuidedTourStep[] = [
  {
    id: 'welcome',
    title: 'Build your first deck',
    instruction: 'You will make one simple template, connect its title to data, add a second card, and check the result in Render. This practice project is separate from your other work.',
    target: '[data-tour-target="workspace-tabs"]',
    continueLabel: 'Start building',
    manual: true,
  },
  {
    id: 'add-shape',
    title: 'Add a background shape',
    instruction: 'Choose Rectangle. It will become the first visual layer in your template.',
    target: '[data-tour-target="tool-rectangle"]',
    tab: 'design',
  },
  {
    id: 'size-shape',
    title: 'Shape the card background',
    instruction: 'Drag the rectangle and its corner handles until it covers most of the card, leaving a small border. Exact measurements are not required.',
    target: '[data-tour-target="design-canvas"]',
    tab: 'design',
  },
  {
    id: 'add-text',
    title: 'Add the card title',
    instruction: 'Choose Text. The new text layer will be selected so you can edit its properties.',
    target: '[data-tour-target="tool-text"]',
    tab: 'design',
  },
  {
    id: 'place-text',
    title: 'Place the card title',
    instruction: 'Move the text near the top of the card and widen its box across the background. Leave a little space around the words.',
    target: '[data-tour-target="design-canvas"]',
    tab: 'design',
  },
  {
    id: 'bind-text',
    title: 'Connect the title to data',
    instruction: 'Find Text in Properties, choose Bind, then Create new. Name the text variable Title.',
    target: '[data-tour-target="bind-text"]',
    tab: 'design',
  },
  {
    id: 'open-data',
    title: 'Open the data grid',
    instruction: 'Choose Data at the top. Each row in this grid becomes one rendered card.',
    target: '[data-tour-target="tab-data"]',
  },
  {
    id: 'add-card',
    title: 'Add a second card',
    instruction: 'Choose Add card. Both rows use the same template, but they can hold different values.',
    target: '[data-tour-target="add-card"]',
    tab: 'data',
  },
  {
    id: 'fill-titles',
    title: 'Give the cards different titles',
    instruction: 'Edit the Title cell in both rows. Try FIRST LIGHT and LAST LIGHT, or use two names of your own.',
    target: '[data-tour-target="data-grid"]',
    tab: 'data',
  },
  {
    id: 'open-render',
    title: 'Open the finished deck',
    instruction: 'Choose Render to combine your template and the two data rows.',
    target: '[data-tour-target="tab-render"]',
  },
  {
    id: 'finish',
    title: 'Check before printing',
    instruction: 'Preflight checks the whole deck for blocking print problems. You can preview cards, download a sample PNG, or arrange print sheets here.',
    target: '[data-tour-target="render-preflight"]',
    tab: 'render',
    continueLabel: 'Finish tour',
    manual: true,
  },
]

const currentStorage = (): StorageLike | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

const validSession = (value: unknown): value is GuidedTourSession => {
  if (!value || typeof value !== 'object') return false
  const session = value as Partial<GuidedTourSession>
  return typeof session.projectId === 'string'
    && session.projectId.startsWith('tutorial_')
    && Number.isInteger(session.stepIndex)
    && Number(session.stepIndex) >= 0
    && Number(session.stepIndex) < GUIDED_TOUR_STEPS.length
    && typeof session.updatedAt === 'string'
}

export const loadGuidedTourSession = (storage: StorageLike | null = currentStorage()) => {
  if (!storage) return null
  try {
    const raw = storage.getItem(GUIDED_TOUR_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return validSession(parsed) ? parsed : null
  } catch {
    return null
  }
}

export const saveGuidedTourSession = (session: GuidedTourSession, storage: StorageLike | null = currentStorage()) => {
  if (!storage) return false
  try {
    storage.setItem(GUIDED_TOUR_STORAGE_KEY, JSON.stringify(session))
    return true
  } catch {
    return false
  }
}

export const clearGuidedTourSession = (storage: StorageLike | null = currentStorage()) => {
  if (!storage) return false
  try {
    storage.removeItem(GUIDED_TOUR_STORAGE_KEY)
    return true
  } catch {
    return false
  }
}

export const newGuidedTourSession = (projectId: string, stepIndex = 0): GuidedTourSession => ({
  projectId,
  stepIndex,
  updatedAt: new Date().toISOString(),
})

export const createGuidedTourProject = (projectId = newId('tutorial')) => {
  const project = createDeckProject('My guided deck', 'poker-bgm', 'empty', true)
  project.id = projectId
  project.card.background = '#F4E9D7'
  project.card.borderEnabled = true
  project.card.borderColor = '#25251F'
  project.card.borderWidthMm = 0.8
  project.card.borderInnerRadiusMm = 1.5
  return project
}

export const guidedTourTitleVariableId = (project: DeckProject) => {
  for (const element of project.elements) {
    if (element.type !== 'text' || !element.bindings.text) continue
    const variable = project.variables.find((candidate) => candidate.id === element.bindings.text && candidate.type === 'text')
    if (variable) return variable.id
  }
  return null
}

const guidedTourBackgroundReady = (project: DeckProject) => {
  const element = project.elements.find((candidate) => candidate.type === 'rectangle')
  if (!element) return false
  return element.x >= 0
    && element.y >= 0
    && element.x <= project.card.widthMm * 0.15
    && element.y <= project.card.heightMm * 0.15
    && element.width >= project.card.widthMm * 0.78
    && element.height >= project.card.heightMm * 0.72
    && element.x + element.width <= project.card.widthMm
    && element.y + element.height <= project.card.heightMm
}

const guidedTourTitlePlaced = (project: DeckProject) => {
  const background = project.elements.find((candidate) => candidate.type === 'rectangle')
  const title = project.elements.find((candidate) => candidate.type === 'text')
  if (!background || !title) return false
  return title.x >= background.x
    && title.y >= background.y
    && title.x <= project.card.widthMm * 0.13
    && title.y <= background.y + background.height * 0.35
    && title.width >= project.card.widthMm * 0.72
    && title.x + title.width <= background.x + background.width
    && title.y + title.height <= background.y + background.height
}

export const guidedTourStepComplete = (stepId: GuidedTourStepId, project: DeckProject, activeTab: WorkspaceTab) => {
  if (stepId === 'welcome' || stepId === 'finish') return true
  if (stepId === 'add-shape') return project.elements.some((element) => element.type === 'rectangle')
  if (stepId === 'size-shape') return guidedTourBackgroundReady(project)
  if (stepId === 'add-text') return project.elements.some((element) => element.type === 'text')
  if (stepId === 'place-text') return guidedTourTitlePlaced(project)
  if (stepId === 'bind-text') return Boolean(guidedTourTitleVariableId(project))
  if (stepId === 'open-data') return activeTab === 'data'
  if (stepId === 'add-card') return project.rows.length >= 2
  if (stepId === 'fill-titles') {
    const variableId = guidedTourTitleVariableId(project)
    if (!variableId || project.rows.length < 2) return false
    const values = project.rows.slice(0, 2).map((row) => String(row.values[variableId] ?? '').trim())
    return values.every(Boolean) && values[0].toLocaleLowerCase() !== values[1].toLocaleLowerCase()
  }
  if (stepId === 'open-render') return activeTab === 'render'
  return false
}

export const isGuidedTourProjectId = (projectId: string) => projectId.startsWith('tutorial_')
